// Utility functions for video URL parsing and FPS handling

/**
 * Parses a video string that contains both video URL and FPS information
 * Format: "VIDEO:url,FPS:value" or VIDEO:url,FPS:value (with or without quotes)
 * @param {string} videoString - The string containing video URL and FPS value
 * @returns {object} Object with videoUrl and fpsValue properties
 */
export const parseVideoString = (videoString) => {
  if (!videoString || typeof videoString !== 'string') {
    return null;
  }

  // Remove surrounding quotes if present
  let cleanString = videoString.trim();
  if ((cleanString.startsWith('"') && cleanString.endsWith('"')) || 
      (cleanString.startsWith("'") && cleanString.endsWith("'"))) {
    cleanString = cleanString.slice(1, -1);
  }

  const parts = cleanString.split(',');
  let videoUrl = null;
  let fpsValue = null;

  for (const part of parts) {
    const trimmedPart = part.trim();
    if (trimmedPart.startsWith('VIDEO:')) {
      videoUrl = trimmedPart.substring(6); // Remove "VIDEO:" prefix
    } else if (trimmedPart.startsWith('FPS:')) {
      fpsValue = trimmedPart.substring(4); // Remove "FPS:" prefix
    }
  }

  return {
    videoUrl,
    fpsValue
  };
};

/**
 * Parses and validates FPS value from a string
 * @param {string} fpsValue - The FPS value as a string
 * @returns {number} The validated FPS value
 */
export const parseFPS = (fpsValue) => {
  if (!fpsValue || typeof fpsValue !== 'string') {
    throw new Error('FPS value is required');
  }

  const fps = parseFloat(fpsValue);
  
  if (isNaN(fps)) {
    throw new Error('Invalid FPS value: must be a number');
  }
  
  if (fps <= 0) {
    throw new Error('FPS must be a positive number');
  }
  
  if (fps > 1000) {
    throw new Error('FPS value seems unreasonably high');
  }
  
  return fps;
};

/**
 * Validates if a string matches the video format
 * @param {string} videoString - The string to validate
 * @returns {boolean} True if the string matches the format
 */
export const isVideoFormat = (videoString) => {
  if (!videoString || typeof videoString !== 'string') {
    return false;
  }
  
  // Remove surrounding quotes if present
  let cleanString = videoString.trim();
  if ((cleanString.startsWith('"') && cleanString.endsWith('"')) || 
      (cleanString.startsWith("'") && cleanString.endsWith("'"))) {
    cleanString = cleanString.slice(1, -1);
  }
  
  return cleanString.includes('VIDEO:') && cleanString.includes('FPS:');
}; 